import { LayoutDashboard, Settings, Truck, Bell, MapPin, Users, CreditCard, FileText, Wrench, Smartphone, BarChart3, UserCircle, IdCard, FileBarChart, Camera, Package, Gauge, Map, Activity } from "lucide-react";
import { version } from "../../../package.json";
import { NavLink } from "@/components/NavLink";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useResellerAccess } from "@/hooks/useResellerAccess";
import { useBranding } from "@/hooks/useBranding";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

export function AppSidebar() {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { isReseller } = useResellerAccess();
  const { title: brandTitle } = useBranding();

  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId');
  const reportsUrl = deviceId ? `/reports?deviceId=${deviceId}` : '/reports';

  const isActive = (path: string) => currentPath === path.split('?')[0];

  const mainItems = [
    { titleKey: "nav.dashboard", url: "/", icon: LayoutDashboard },
    { titleKey: "nav.liveMap", url: "/live-map", icon: Map },
    { titleKey: "nav.fleetVehicles", url: "/devices", icon: Truck },
    { titleKey: "nav.cameras", url: "/cameras", icon: Camera },
    { titleKey: "nav.reports", url: reportsUrl, icon: FileBarChart },
    { titleKey: "nav.performanceReport", url: "/performance-report", icon: Gauge },
    { titleKey: "nav.activity", url: "/history/trips", icon: Activity },
  ];


  const resellerItems = [
    { titleKey: "nav.overview", url: "/reseller", icon: BarChart3 },
    { titleKey: "nav.customers", url: "/customers", icon: Users },
    { titleKey: "nav.deviceStock", url: "/device-stock", icon: Package },
    { titleKey: "nav.simCards", url: "/sim-cards", icon: Smartphone },
    { titleKey: "nav.contracts", url: "/contracts", icon: FileText },
    { titleKey: "nav.invoices", url: "/invoices", icon: CreditCard },
    { titleKey: "nav.fieldServices", url: "/field-services", icon: Wrench },
  ];

  const secondaryItems = [
    { titleKey: "nav.users", url: "/users", icon: UserCircle },
    { titleKey: "nav.drivers", url: "/drivers", icon: IdCard },
    { titleKey: "nav.geofences", url: "/geofences", icon: MapPin },
    { titleKey: "nav.notifications", url: "/notifications", icon: Bell },
    { titleKey: "nav.preferences", url: "/settings", icon: Settings },
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.fleet")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={t(item.titleKey)}
                  >
                    <NavLink
                      to={item.url}
                      end
                      className="flex items-center gap-2"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{t(item.titleKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isReseller && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.reseller")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {resellerItems.map((item) => (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={t(item.titleKey)}
                    >
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-2"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{t(item.titleKey)}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.settings")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={t(item.titleKey)}
                  >
                    <NavLink
                      to={item.url}
                      end
                      className="flex items-center gap-2"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{t(item.titleKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        {!collapsed && (
          <div className="text-xs text-muted-foreground">
            <p className="flex justify-between">© 2025 {brandTitle || t('login.title')}<span className="opacity-50">v{version}</span></p>

          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
